import React from 'react';
import { useQuery } from 'react-query';
import { CheckCircleOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { auth } from '../../../sp-firebase/firebase';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

// ── Types (mirror of services/api/src/recommendations/engine.ts) ──

interface OptionLeg {
  direction: 'buy' | 'sell';
  optionType: 'call' | 'put';
  strike: number;
  strikeOffsetPct: number;
}

interface RecommendedAction {
  instrumentType: 'cylinder' | 'forward';
  notionalValue: number;
  quantityMt: number;
  maturity: string;
  referenceForward: number;
  optionLegs?: OptionLeg[];
  indicativePremium?: string;
  rationale: string;
}

interface PeriodRecommendation {
  period: string;
  periodStart: string;
  exposureMt: number;
  exposureValue: number;
  existingHedgeMt: number;
  existingHedgeValue: number;
  existingHedgeRatio: number;
  targetMt: number;
  targetValue: number;
  gapMt: number;
  gapValue: number;
  forwardPrice: number;
  recommendedActions: RecommendedAction[];
  expectedHedgeRatioAfter: number;
}

interface Recommendation {
  commodity: string;
  formulaId: number;
  currency: string;
  unit: string;
  generatedAt: string;
  marketDataAsOf: string | null;
  spotPrice: number | null;
  exposureHorizonQuarters: number;
  policy: {
    targetHedgeRatio: number;
    cylinderShare: number;
    forwardShare: number;
    callOffsetPct: number;
    putOffsetPct: number;
  };
  totalExposureMt: number;
  totalExposureValue: number;
  currentState: { hedgeRatio: number; hedgedMt: number; hedgedValue: number; openMt: number };
  targetState: { hedgeRatio: number; hedgedMt: number; openMt: number };
  afterState: { hedgeRatio: number; hedgedMt: number };
  policyStatus: 'no_action_required' | 'action_recommended';
  rationale: string[];
  periods: PeriodRecommendation[];
}

// ── Fetch ─────────────────────────────────────────────────────

async function fetchRecommendation(accountId: number, formulaId: number): Promise<Recommendation | null> {
  const user = auth.currentUser;
  if (!user) return null;
  const token = await user.getIdToken();
  const res = await fetch(
    `${API_URL}/api/v2/recommendations?accountId=${accountId}&formulaId=${formulaId}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) return null;
  const data = await res.json();
  return data.recommendation || null;
}

// ── Formatting helpers ────────────────────────────────────────

const fmtMoney = (v: number): string => {
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${Math.round(v / 1_000)}K`;
  return `$${Math.round(v)}`;
};
const fmtPct = (r: number) => `${Math.round(r * 100)}%`;
const fmtMt = (v: number) => `${v.toLocaleString()} MT`;
const fmtPrice = (v: number) => `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// ── Strategy metadata ─────────────────────────────────────────

type StrategyKey = 'cylinder' | 'forward' | 'open';

const STRATEGY_META: Record<
  StrategyKey,
  { name: string; barColor: string; textColor: string; tagline: string; why: string }
> = {
  cylinder: {
    name: 'Collar',
    barColor: 'bg-blue-400',
    textColor: 'text-[#1D4ED8]',
    tagline: 'Protect against large price increases, while still benefiting from moderate price declines.',
    why: 'because it protects against significant price increases while preserving some benefit if prices fall — and the sold put finances the purchased call, so it typically costs near zero.',
  },
  forward: {
    name: 'Forward',
    barColor: 'bg-[#0D9488]',
    textColor: 'text-[#0F766E]',
    tagline: 'Your purchase price is locked, regardless of where the market trades.',
    why: 'to create certainty over part of the purchasing cost.',
  },
  open: {
    name: 'Open',
    barColor: 'bg-gray-200',
    textColor: 'text-gray-600',
    tagline: 'You remain fully exposed — benefiting from price declines but also exposed to increases.',
    why: 'to maintain flexibility and participation in lower prices.',
  },
};

// ── Payoff diagram (large, business-friendly) ─────────────────
// x = market price, y = effective purchase price (higher = costs more)

const PayoffDiagram: React.FC<{
  kind: StrategyKey;
  put?: number;
  forward?: number;
  call?: number;
}> = ({ kind, put, forward, call }) => {
  // Chart area: x 40→420, y 25→130 (y grows downward; lower on screen = cheaper)
  const X = { left: 40, put: 140, fwd: 230, call: 320, right: 420 };
  const Y = { top: 25, cap: 50, mid: 78, floor: 108, axis: 132 };

  return (
    <svg viewBox="0 0 440 175" className="w-full" style={{ maxHeight: 240 }}>
      {/* three economic zones (cylinder) */}
      {kind === 'cylinder' && (
        <>
          <rect x={X.left} y={Y.top - 4} width={X.put - X.left} height={Y.axis - Y.top + 4} fill="#fffbeb" />
          <rect x={X.put} y={Y.top - 4} width={X.call - X.put} height={Y.axis - Y.top + 4} fill="#f0fdf4" />
          <rect x={X.call} y={Y.top - 4} width={X.right - X.call} height={Y.axis - Y.top + 4} fill="#eff6ff" />
          <text x={(X.left + X.put) / 2} y={Y.top + 8} fontSize="8.5" fontWeight="700" fill="#b45309" textAnchor="middle">
            BENEFIT STOPS HERE
          </text>
          <text x={(X.put + X.call) / 2} y={Y.top + 8} fontSize="8.5" fontWeight="700" fill="#15803d" textAnchor="middle">
            YOU FOLLOW THE MARKET
          </text>
          <text x={(X.call + X.right) / 2} y={Y.top + 8} fontSize="8.5" fontWeight="700" fill="#1d4ed8" textAnchor="middle">
            PROTECTED
          </text>
        </>
      )}
      {/* axes */}
      <line x1={X.left} y1={Y.axis} x2={X.right} y2={Y.axis} stroke="#d1d5db" strokeWidth="1" />
      <line x1={X.left} y1={Y.top - 8} x2={X.left} y2={Y.axis} stroke="#d1d5db" strokeWidth="1" />
      <text x={X.left - 4} y={Y.top - 12} fontSize="9" fill="#9ca3af">
        Effective purchase price
      </text>
      <text x={X.right} y={Y.axis + 12} fontSize="9" fill="#9ca3af" textAnchor="end">
        Market price →
      </text>

      {/* unhedged market cost, for reference */}
      <line x1={X.left} y1={Y.floor + 14} x2={X.right} y2={Y.cap - 16} stroke="#e5e7eb" strokeWidth="1.5" strokeDasharray="4,3" />
      {kind === 'cylinder' && (
        <text x={X.right - 2} y={Y.cap - 20} fontSize="8" fill="#c4c8ce" textAnchor="end">
          unhedged
        </text>
      )}

      {kind === 'cylinder' && (
        <>
          {/* payoff: floor → market → cap */}
          <path
            d={`M${X.left},${Y.floor} H${X.put} L${X.call},${Y.cap} H${X.right}`}
            stroke="#2563eb"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
          />
          <text x={(X.left + X.put) / 2} y={Y.floor + 14} fontSize="10" fontWeight="700" fill="#2563eb" textAnchor="middle">
            FLOOR
          </text>
          <text x={(X.call + X.right) / 2} y={Y.cap - 8} fontSize="10" fontWeight="700" fill="#2563eb" textAnchor="middle">
            CAP
          </text>
          {/* strike markers */}
          {[
            { x: X.put, value: put, label: 'Put strike' },
            { x: X.fwd, value: forward, label: 'Forward' },
            { x: X.call, value: call, label: 'Call strike' },
          ].map((m) => (
            <g key={m.label}>
              <line x1={m.x} y1={Y.top} x2={m.x} y2={Y.axis} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="2,3" />
              <text x={m.x} y={Y.axis + 14} fontSize="10" fontWeight="700" fill="#374151" textAnchor="middle">
                {m.value ? fmtPrice(m.value) : ''}
              </text>
              <text x={m.x} y={Y.axis + 26} fontSize="9" fill="#9ca3af" textAnchor="middle">
                {m.label}
              </text>
            </g>
          ))}
        </>
      )}

      {kind === 'forward' && (
        <>
          <line x1={X.left} y1={Y.mid} x2={X.right} y2={Y.mid} stroke="#0ea5e9" strokeWidth="3" strokeLinecap="round" />
          <text x={(X.left + X.right) / 2} y={Y.mid - 10} fontSize="11" fontWeight="700" fill="#0284c7" textAnchor="middle">
            {forward ? `${fmtPrice(forward)} — fixed` : 'fixed price'}
          </text>
        </>
      )}

      {kind === 'open' && (
        <>
          <line x1={X.left} y1={Y.floor + 14} x2={X.right} y2={Y.cap - 16} stroke="#6b7280" strokeWidth="3" strokeLinecap="round" />
          <text x={(X.left + X.right) / 2} y={Y.mid - 14} fontSize="10" fontWeight="700" fill="#6b7280" textAnchor="middle" transform={`rotate(-8 ${(X.left + X.right) / 2} ${Y.mid - 14})`}>
            follows the market
          </text>
        </>
      )}
    </svg>
  );
};

// ── Scenario strip (down / flat / up) ─────────────────────────

const ScenarioStrip: React.FC<{ kind: StrategyKey; commodity: string; put?: number; call?: number }> = ({
  kind,
  commodity,
  put,
  call,
}) => {
  const scenarios: Record<StrategyKey, { title: string; text: string }[]> = {
    cylinder: [
      { title: `${commodity} falls`, text: `You benefit from lower prices down to the Put level${put ? ` (${fmtPrice(put)})` : ''}.` },
      { title: 'Stays around current levels', text: 'You follow the market — no cap, no floor applies.' },
      { title: `${commodity} rises`, text: `Your price is protected above the Call level${call ? ` (${fmtPrice(call)})` : ''}.` },
    ],
    forward: [
      { title: `${commodity} falls`, text: 'You still pay the locked price — no benefit from the decline.' },
      { title: 'Stays around current levels', text: 'You pay the locked price, close to the market.' },
      { title: `${commodity} rises`, text: 'You are fully protected — the locked price does not move.' },
    ],
    open: [
      { title: `${commodity} falls`, text: 'You benefit fully from the lower purchase prices.' },
      { title: 'Stays around current levels', text: 'You pay the market price.' },
      { title: `${commodity} rises`, text: 'Your costs rise with the market — this portion is unprotected.' },
    ],
  };
  return (
    <div className="grid grid-cols-3 gap-3 mt-2">
      {scenarios[kind].map((s, i) => (
        <div key={i} className="bg-gray-50 rounded-md px-3 py-2">
          <div className="text-xs font-bold flex items-center gap-1">
            {i === 0 ? '↓' : i === 1 ? '→' : '↑'} {s.title}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">{s.text}</div>
        </div>
      ))}
    </div>
  );
};

// ── Big interactive allocation bar ────────────────────────────

const AllocationBar: React.FC<{
  shares: Record<StrategyKey, number>;
  targetRatio: number;
  selected: StrategyKey;
  onSelect: (_k: StrategyKey) => void;
}> = ({ shares, targetRatio, selected, onSelect }) => (
  <div>
    {/* protected-vs-open bracket */}
    <div className="flex text-[11px] font-bold mb-0.5">
      <div style={{ width: `${targetRatio * 100}%` }} className="text-center text-[#15803D]">
        {fmtPct(targetRatio)} PROTECTED ✓
      </div>
      <div style={{ width: `${(1 - targetRatio) * 100}%` }} className="text-center text-gray-400">
        OPEN
      </div>
    </div>
    <div className="flex mb-1.5">
      <div className="border-l-2 border-r-2 border-t-2 border-[#16A34A]/40 rounded-t h-2" style={{ width: `${targetRatio * 100}%` }} />
      <div className="h-2" style={{ width: `${(1 - targetRatio) * 100}%` }} />
    </div>
    <div className="flex h-14 rounded-lg overflow-hidden border border-gray-200">
      {(['cylinder', 'forward', 'open'] as StrategyKey[]).map((k) => (
        <button
          key={k}
          onClick={() => onSelect(k)}
          style={{ width: fmtPct(shares[k]) }}
          className={`${STRATEGY_META[k].barColor} flex flex-col items-center justify-center transition-all border-r-2 border-white last:border-r-0 ${
            selected === k ? 'ring-2 ring-inset ring-black/30 brightness-110' : 'hover:brightness-105'
          }`}
        >
          <span className={`text-base font-bold ${k === 'open' ? 'text-gray-600' : 'text-white'}`}>
            {fmtPct(shares[k])}
          </span>
          <span className={`text-[10px] font-semibold uppercase tracking-wide ${k === 'open' ? 'text-gray-500' : 'text-white/90'}`}>
            {STRATEGY_META[k].name}
          </span>
        </button>
      ))}
    </div>
    <div className="text-center text-xs font-semibold text-gray-500 mt-1.5">
      {fmtPct(shares.cylinder)} Collar · {fmtPct(shares.forward)} Forward · {fmtPct(shares.open)} Open
      <span className="text-gray-300 font-normal"> — shares of total exposure</span>
    </div>
  </div>
);

// ── Hedge program over time (100% stacked rows per quarter) ──

const HedgeProgramChart: React.FC<{
  periods: PeriodRecommendation[];
  selected: string | null;
  onSelect: (_p: string) => void;
}> = ({ periods, selected, onSelect }) => (
  <div className="flex flex-col gap-1">
    {periods.map((p) => {
      const existingPct = Math.min(p.existingHedgeRatio, 1) * 100;
      const addedPct = (p.gapValue / Math.max(p.exposureValue, 1)) * 100;
      const openPct = Math.max(100 - existingPct - addedPct, 0);
      const isSelected = selected === p.periodStart;
      return (
        <button
          key={p.periodStart}
          onClick={() => onSelect(p.periodStart)}
          className={`flex items-center gap-3 rounded-md px-2 py-1.5 transition-colors text-left ${
            isSelected ? 'bg-[#EFF6FF]' : 'hover:bg-gray-50'
          }`}
        >
          <span className="text-xs font-semibold w-12 flex-shrink-0">{p.period.replace(' 20', ' ')}</span>
          <div className="flex-1 flex h-6 rounded overflow-hidden bg-gray-50">
            {existingPct > 0 && (
              <div className="bg-[#16A34A] flex items-center justify-center" style={{ width: `${existingPct}%` }} title="Existing protection">
                {existingPct > 12 && <span className="text-[10px] font-bold text-white">{fmtMoney(p.existingHedgeMt * p.forwardPrice)}</span>}
              </div>
            )}
            {addedPct > 0 && (
              <div className="bg-blue-400 flex items-center justify-center" style={{ width: `${addedPct}%` }} title="Recommended additional protection">
                {addedPct > 12 && <span className="text-[10px] font-bold text-white">+{fmtMoney(p.gapValue)}</span>}
              </div>
            )}
            <div className="bg-gray-110 flex items-center justify-center" style={{ width: `${openPct}%` }} title="Remains open">
              {openPct > 14 && (
                <span className="text-[10px] font-semibold text-gray-500">
                  {fmtMoney(p.exposureValue * (openPct / 100))} open
                </span>
              )}
            </div>
          </div>
          <span className="text-xs w-20 text-right flex-shrink-0">
            <span className="font-bold text-[#15803D]">{fmtPct(Math.min(p.expectedHedgeRatioAfter, 1))}</span>
            <span className="text-gray-400"> of {fmtMoney(p.exposureValue)}</span>
          </span>
        </button>
      );
    })}
  </div>
);

// ── Selected quarter detail panel (right side) ────────────────

const QuarterPanel: React.FC<{ period: PeriodRecommendation; commodity: string }> = ({ period, commodity }) => {
  const [showTrades, setShowTrades] = React.useState(false);
  const [quoteRequested, setQuoteRequested] = React.useState(false);
  const cylinder = period.recommendedActions.find((a) => a.instrumentType === 'cylinder');
  const forward = period.recommendedActions.find((a) => a.instrumentType === 'forward');
  const call = cylinder?.optionLegs?.find((l) => l.optionType === 'call');
  const put = cylinder?.optionLegs?.find((l) => l.optionType === 'put');

  // Reset panel state when switching quarters
  React.useEffect(() => {
    setShowTrades(false);
    setQuoteRequested(false);
  }, [period.periodStart]);

  const splitSummary = period.recommendedActions
    .map((a) => `${fmtMoney(a.notionalValue)} ${a.instrumentType === 'cylinder' ? 'Collar' : 'Forward'}`)
    .join(' + ');

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-125 flex flex-col h-full overflow-auto">
      <div className="px-4 py-3 border-b border-gray-125">
        <div className="text-xs font-bold uppercase tracking-wide text-gray-400">
          {period.period} · {commodity}
        </div>
        {period.gapValue > 0 ? (
          <>
            <div className="text-xl font-bold mt-1 leading-tight">
              Add <span className="text-[#D97706]">{fmtMoney(period.gapValue)}</span> of protection
            </div>
            <div className="text-sm font-semibold text-gray-600 mt-0.5">{splitSummary}</div>
          </>
        ) : (
          <div className="text-xl font-bold mt-1 leading-tight text-[#15803D]">No action required</div>
        )}
      </div>
      <div className="p-4 flex flex-col gap-3">
        {period.recommendedActions.length > 0 ? (
          <>
            {/* Primary workflow CTA + subtle details toggle */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setQuoteRequested(true)}
                disabled={quoteRequested}
                className={`flex-1 text-sm font-bold rounded-md py-2 transition-opacity ${
                  quoteRequested
                    ? 'bg-[#F0FDF4] text-[#15803D] border border-[#BBE7C6] cursor-default'
                    : 'bg-red text-white hover:opacity-90'
                }`}
              >
                {quoteRequested ? 'Quote requested ✓' : 'Request quote'}
              </button>
              <button
                onClick={() => setShowTrades(!showTrades)}
                className="text-xs text-gray-500 hover:text-gray-700 underline decoration-dotted whitespace-nowrap"
              >
                {showTrades
                  ? 'Hide details ▴'
                  : `View the ${period.recommendedActions.length > 1 ? `${period.recommendedActions.length} trades` : 'trade'} ▾`}
              </button>
            </div>
            {quoteRequested && (
              <div className="text-[11px] text-gray-400 -mt-1">
                Indicative quote request drafted for review — broker execution workflow is not part of this demo.
              </div>
            )}
            {!showTrades && (
              <button
                onClick={() => setShowTrades(true)}
                className="text-left text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-md px-3 py-2 hover:bg-gray-75 transition-colors"
              >
                {cylinder && call && put && (
                  <div>
                    <b className="text-[#1D4ED8]">Collar</b> — buy Call {fmtPrice(call.strike)} / sell Put{' '}
                    {fmtPrice(put.strike)}, {fmtMt(cylinder.quantityMt)}, exp. {cylinder.maturity.slice(0, 7)}
                  </div>
                )}
                {forward && (
                  <div className={cylinder ? 'mt-0.5' : ''}>
                    <b className="text-[#0F766E]">Forward</b> — buy at {fmtPrice(forward.referenceForward)}/MT,{' '}
                    {fmtMt(forward.quantityMt)}, exp. {forward.maturity.slice(0, 7)}
                  </div>
                )}
              </button>
            )}
            {showTrades && (
            <div className="flex flex-col gap-3">
              {cylinder && call && put && (
                <div className="rounded-md border border-[#BFDBFE] bg-[#EFF6FF] p-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-bold text-[#1D4ED8]">Collar</span>
                    <span className="text-base font-bold">{fmtMoney(cylinder.notionalValue)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-gray-600 mt-1.5">
                    <span>Quantity</span>
                    <span className="text-right font-semibold">{fmtMt(cylinder.quantityMt)}</span>
                    <span>Buy Call ({call.strikeOffsetPct > 0 ? '+' : ''}{call.strikeOffsetPct}%)</span>
                    <span className="text-right font-semibold">{fmtPrice(call.strike)}</span>
                    <span>Sell Put ({put.strikeOffsetPct}%)</span>
                    <span className="text-right font-semibold">{fmtPrice(put.strike)}</span>
                    <span>Reference forward</span>
                    <span className="text-right font-semibold">{fmtPrice(cylinder.referenceForward)}/MT</span>
                    <span>Expiry</span>
                    <span className="text-right font-semibold">{cylinder.maturity.slice(0, 7)}</span>
                    <span>Indicative premium</span>
                    <span className="text-right font-semibold text-[#15803D]">{cylinder.indicativePremium}</span>
                  </div>
                </div>
              )}
              {forward && (
                <div className="rounded-md border border-[#99F6E4] bg-[#F0FDFA] p-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm font-bold text-[#0F766E]">Forward</span>
                    <span className="text-base font-bold">{fmtMoney(forward.notionalValue)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-gray-600 mt-1.5">
                    <span>Quantity</span>
                    <span className="text-right font-semibold">{fmtMt(forward.quantityMt)}</span>
                    <span>Indicative price</span>
                    <span className="text-right font-semibold">{fmtPrice(forward.referenceForward)}/MT</span>
                    <span>Expiry</span>
                    <span className="text-right font-semibold">{forward.maturity.slice(0, 7)}</span>
                  </div>
                </div>
              )}
            </div>
            )}
            <div className="flex items-center gap-1.5 text-sm text-[#15803D] font-semibold">
              <CheckCircleOutlined sx={{ fontSize: 16 }} />
              Expected result: {fmtPct(period.expectedHedgeRatioAfter)} protected
            </div>
            {/* Supporting calculation — secondary */}
            <div className="border-t border-gray-100 pt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500">
              <span>Exposure</span>
              <span className="text-right font-semibold text-gray-700">{fmtMoney(period.exposureValue)}</span>
              <span>Existing protection</span>
              <span className="text-right font-semibold text-gray-700">
                {fmtMoney(period.existingHedgeMt * period.forwardPrice)} ({fmtPct(period.existingHedgeRatio)})
              </span>
              <span>Policy target</span>
              <span className="text-right font-semibold text-gray-700">{fmtMoney(period.targetValue)}</span>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 text-sm text-[#15803D]">
            <CheckCircleOutlined sx={{ fontSize: 16 }} />
            {period.period} is already within policy.
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────

interface Props {
  accountId: number;
  formulaId: number | null;
}

export const HedgingRecommendations: React.FC<Props> = ({ accountId, formulaId }) => {
  const [selectedStrategy, setSelectedStrategy] = React.useState<StrategyKey>('cylinder');
  const [selectedPeriod, setSelectedPeriod] = React.useState<string | null>(null);
  const [showMethodology, setShowMethodology] = React.useState(false);

  const { data: reco, isLoading } = useQuery(
    ['hedging-recommendation', accountId, formulaId],
    () => fetchRecommendation(accountId, formulaId!),
    { enabled: !!accountId && !!formulaId, staleTime: 5 * 60 * 1000 },
  );

  // Default-select the first quarter that requires action
  React.useEffect(() => {
    if (reco && !selectedPeriod) {
      const first = reco.periods.find((p) => p.gapMt > 0) || reco.periods[0];
      if (first) setSelectedPeriod(first.periodStart);
    }
  }, [reco, selectedPeriod]);

  if (!formulaId) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
        Select a material above to see its hedging recommendation.
      </div>
    );
  }
  if (isLoading) {
    return <div className="flex items-center justify-center h-48 text-gray-400 text-sm">Generating recommendation…</div>;
  }
  if (!reco || reco.periods.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
        No forecast exposure found for this material.
      </div>
    );
  }

  const actionNeeded = reco.policyStatus === 'action_recommended';
  const openShare = 1 - reco.policy.targetHedgeRatio;
  const gapValue = reco.periods.reduce((s, p) => s + p.gapValue, 0);
  const shares: Record<StrategyKey, number> = {
    cylinder: reco.policy.cylinderShare,
    forward: reco.policy.forwardShare,
    open: openShare,
  };

  // Representative strikes for the payoff diagrams (first quarter with a cylinder)
  const refCylinder = reco.periods.flatMap((p) => p.recommendedActions).find((a) => a.instrumentType === 'cylinder');
  const refCall = refCylinder?.optionLegs?.find((l) => l.optionType === 'call');
  const refPut = refCylinder?.optionLegs?.find((l) => l.optionType === 'put');
  const refForward = refCylinder?.referenceForward ?? reco.spotPrice ?? undefined;

  const selectedPeriodData = reco.periods.find((p) => p.periodStart === selectedPeriod) || reco.periods[0];

  return (
    <div className="flex flex-col gap-4 pb-4">
      {/* ═══ A. RECOMMENDATION HERO ═══ */}
      <div className={`rounded-xl border-2 p-5 ${actionNeeded ? 'bg-gradient-to-r from-[#FFF8ED] to-white border-[#FDE1B0]' : 'bg-gradient-to-r from-[#F0FDF4] to-white border-[#BBE7C6]'}`}>
        <div className="flex gap-8 items-start">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {actionNeeded ? (
                <WarningAmberOutlined className="text-[#F59E0B]" sx={{ fontSize: 22 }} />
              ) : (
                <CheckCircleOutlined className="text-[#16A34A]" sx={{ fontSize: 22 }} />
              )}
              <span className="text-xs font-bold uppercase tracking-widest text-gray-400">{reco.commodity}</span>
            </div>
            {actionNeeded ? (
              <h2 className="text-2xl font-bold leading-tight">
                We recommend protecting an additional{' '}
                <span className="text-[#D97706]">{fmtMoney(gapValue)}</span>
              </h2>
            ) : (
              <h2 className="text-2xl font-bold leading-tight">
                Your exposure is protected in line with policy <span className="text-[#16A34A]">✓</span>
              </h2>
            )}
            <div className="flex gap-6 mt-3 text-sm">
              <span>
                <b className="text-base">{fmtMoney(reco.totalExposureValue)}</b>{' '}
                <span className="text-gray-400">exposure</span>
              </span>
              <span>
                <b className={`text-base ${actionNeeded ? 'text-[#D97706]' : 'text-[#16A34A]'}`}>
                  {fmtPct(reco.currentState.hedgeRatio)}
                </b>{' '}
                <span className="text-gray-400">hedged</span>
              </span>
              <span>
                <b className="text-base">{fmtPct(reco.policy.targetHedgeRatio)}</b>{' '}
                <span className="text-gray-400">policy target</span>
              </span>
            </div>
            <div className="text-xs text-gray-400 mt-2">
              Based on approved forecasts, open positions and the forward curve
              {reco.marketDataAsOf && <> as of {reco.marketDataAsOf.slice(0, 10)}</>}.{' '}
              <button className="text-blue-300 underline decoration-dotted" onClick={() => setShowMethodology(!showMethodology)}>
                How was this calculated?
              </button>
            </div>
            {showMethodology && (
              <div className="text-xs text-gray-600 mt-2 bg-white rounded-md p-3 border border-gray-100 max-w-xl">
                {reco.rationale.map((r, i) => (
                  <p key={i} className="mb-1 last:mb-0">{r}</p>
                ))}
              </div>
            )}
          </div>
          {/* Recommended portfolio — the main visualization */}
          <div className="flex-1 min-w-0 pt-1">
            <div className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-2">
              Recommended allocation of total exposure
            </div>
            <AllocationBar
              shares={shares}
              targetRatio={reco.policy.targetHedgeRatio}
              selected={selectedStrategy}
              onSelect={setSelectedStrategy}
            />
          </div>
        </div>
      </div>

      {/* ═══ B. HOW YOUR PROTECTION WORKS ═══ */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-125 p-4">
        <div className="flex items-baseline justify-between mb-3">
          <span className="text-sm font-bold">How your protection works</span>
          <span className="text-[11px] text-gray-400">select a strategy to see its behavior</span>
        </div>
        <div className="flex gap-4">
          {/* strategy selector tabs */}
          <div className="flex flex-col gap-1.5 w-56 flex-shrink-0">
            {(['cylinder', 'forward', 'open'] as StrategyKey[]).map((k) => (
              <button
                key={k}
                onClick={() => setSelectedStrategy(k)}
                className={`text-left rounded-md border px-3 py-2.5 transition-colors ${
                  selectedStrategy === k
                    ? 'border-blue-300 bg-[#EFF6FF]'
                    : 'border-gray-125 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-bold ${STRATEGY_META[k].textColor}`}>{STRATEGY_META[k].name}</span>
                  <span className="text-lg font-bold text-gray-700">{fmtPct(shares[k])}</span>
                </div>
                <div className="text-[11px] text-gray-400 leading-tight mt-0.5">
                  of total exposure
                  {k !== 'open' && ` · ≈ ${fmtMoney(gapValue * (shares[k] / (shares.cylinder + shares.forward)))} new`}
                </div>
              </button>
            ))}
          </div>
          {/* selected strategy explanation */}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold mb-1">{STRATEGY_META[selectedStrategy].tagline}</div>
            <div className="text-xs text-gray-500 mb-1">
              We recommend {fmtPct(shares[selectedStrategy])} {STRATEGY_META[selectedStrategy].name}{' '}
              {STRATEGY_META[selectedStrategy].why}
            </div>
            <PayoffDiagram
              kind={selectedStrategy}
              put={refPut?.strike}
              forward={refForward}
              call={refCall?.strike}
            />
            {selectedStrategy === 'cylinder' && refPut && refCall && (
              <div className="text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-md px-3 py-2 mt-1">
                You are protected above <b>{fmtPrice(refCall.strike)}/MT</b> and benefit from lower prices down to{' '}
                <b>{fmtPrice(refPut.strike)}/MT</b>. Below {fmtPrice(refPut.strike)}/MT, further price declines no
                longer reduce your effective purchase price on this portion.
              </div>
            )}
            {selectedStrategy === 'forward' && refForward && (
              <div className="text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-md px-3 py-2 mt-1">
                Your purchase price is locked at <b>≈{fmtPrice(refForward)}/MT</b>. You are protected if{' '}
                {reco.commodity.split(' ')[0]} rises, but do not benefit if it falls.
              </div>
            )}
            {selectedStrategy === 'open' && (
              <div className="text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-md px-3 py-2 mt-1">
                This portion remains at market. You benefit fully if prices fall and remain fully exposed if they
                rise.
              </div>
            )}
            <ScenarioStrip
              kind={selectedStrategy}
              commodity={reco.commodity.split(' ')[0]}
              put={refPut?.strike}
              call={refCall?.strike}
            />
          </div>
        </div>
      </div>

      {/* ═══ C+D+E. HEDGE PROGRAM + ACTIONS + DETAIL ═══ */}
      <div className="flex gap-4 items-stretch">
        <div className="flex-[3] min-w-0 flex flex-col gap-4">
          {/* C. timeline */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-125 p-4">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-sm font-bold">Your hedge program over time</span>
              <div className="flex gap-3 text-[11px] text-gray-400">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#16A34A]" /> existing</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-blue-400" /> recommended</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-gray-110" /> remains open</span>
              </div>
            </div>
            <HedgeProgramChart
              periods={reco.periods}
              selected={selectedPeriod}
              onSelect={setSelectedPeriod}
            />
          </div>
          {/* D. compact actions table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-125 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="text-left px-4 py-2 font-medium">Period</th>
                  <th className="text-right px-4 py-2 font-medium">Exposure</th>
                  <th className="text-right px-4 py-2 font-medium">Protected</th>
                  <th className="text-right px-4 py-2 font-medium">Add</th>
                  <th className="text-right px-4 py-2 font-medium">Result</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {reco.periods.map((p) => {
                  const isSelected = selectedPeriod === p.periodStart;
                  return (
                    <tr
                      key={p.periodStart}
                      className={`border-b border-gray-50 last:border-b-0 cursor-pointer ${
                        isSelected ? 'bg-[#EFF6FF]' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedPeriod(p.periodStart)}
                    >
                      <td className="px-4 py-2 font-semibold">{p.period}</td>
                      <td className="px-4 py-2 text-right">{fmtMoney(p.exposureValue)}</td>
                      <td className="px-4 py-2 text-right">{fmtMoney(p.existingHedgeMt * p.forwardPrice)}</td>
                      <td className="px-4 py-2 text-right">
                        {p.gapValue > 0 ? (
                          <span className="text-[#B45309] font-bold">+{fmtMoney(p.gapValue)}</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right font-semibold text-[#15803D]">
                        {fmtPct(Math.min(p.expectedHedgeRatioAfter, 1))}
                      </td>
                      <td className="pr-3 text-right">
                        <span className={`text-xs ${isSelected ? 'text-blue-600 font-semibold' : 'text-gray-300'}`}>
                          {isSelected ? 'viewing' : 'view →'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        {/* E. selected quarter detail */}
        <div className="flex-[2] min-w-0">
          {selectedPeriodData && <QuarterPanel period={selectedPeriodData} commodity={reco.commodity} />}
        </div>
      </div>

      <div className="text-[11px] text-gray-300">
        Recommendations are decision support based on your company&apos;s hedging policy, current positions and latest
        market data. They are not investment advice and require internal review before execution.
      </div>
    </div>
  );
};
